import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  useLoungeAirportLinkUrl,
  useLoungeById,
  useTranslatedAirportLounges,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useRouteParams } from '@/context/routeParamsContext';
import { useUserLoungeBooking } from '@/hooks/waitListHooks/waitListHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { LoungeTypes } from '@/constants';
import { restoreScrollPosition } from '@/helpers/utils';
import { WAITLIST_SCROLL_POSITION_KEY } from '@/constants/waitlistLounges';
import RouteReplaceWithQueryParams from '../common/RouteReplaceWithQueryParams';
import Loading from '../common/Loading';
import LoungeDetailsHeroCarousel from './LoungeDetailsHeroCarousel';
import LoungeDetailsContent from './LoungeDetailsContent';
import { setLoungeDetails } from '../../slices/loungeSlice';

const LoungeDetailsPage = () => {
  const isUnloadingRef = useRef(false);
  const dispatch = useDispatch();
  const { isLoading: isTranslatedAirportLoungesLoading } =
    useTranslatedAirportLounges();
  const { isLoading: isUserLoungeBookingLoading } = useUserLoungeBooking();
  const { product, loungeId } = useRouteParams();
  const airportLinkUrl = useLoungeAirportLinkUrl(loungeId);
  const lounge = useLoungeById(loungeId);
  const {
    config: { features = [] },
  } = useCommonContent();

  const isSidecarEnabled =
    features.some((feat) => feat.name === 'sidecar' && feat.enabled) &&
    !product?.includes('idc');

  const isLoungeDetailsLoading =
    isTranslatedAirportLoungesLoading || isUserLoungeBookingLoading;

  useEffect(() => {
    if (isLoungeDetailsLoading || !lounge) {
      return;
    }

    restoreScrollPosition({
      storageKey: WAITLIST_SCROLL_POSITION_KEY,
    });
  }, [isLoungeDetailsLoading, loungeId, lounge]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      isUnloadingRef.current = true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Keep saved scroll for refresh/new tab workflows, clear on in-app route changes.
      if (!isUnloadingRef.current) {
        window.sessionStorage.removeItem(WAITLIST_SCROLL_POSITION_KEY);
      }
    };
  }, []);

  useEffect(() => {
    if (lounge) {
      dispatch(setLoungeDetails(lounge));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- set lounge details once lounge details are loaded
  }, [isTranslatedAirportLoungesLoading]);

  if (isLoungeDetailsLoading) {
    return (
      <Loading
        size="lg"
        containerClassName="flex flex-justify-center dls-white-bg pad-4 margin-t"
      />
    );
  }

  if (
    !lounge ||
    (lounge.experience_type === LoungeTypes.SIDECAR && !isSidecarEnabled)
  ) {
    return <RouteReplaceWithQueryParams to={`/${airportLinkUrl}`} />;
  }

  return (
    <>
      <LoungeDetailsHeroCarousel />
      <LoungeDetailsContent />
    </>
  );
};

export default LoungeDetailsPage;
